import React, { useState } from 'react';
import axios from 'axios';
import './FileUpload.css';

function FileUpload({ onUploadComplete, sessionId }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    
    // Include existing session_id if available (for re-uploads)
    if (sessionId) {
      formData.append('session_id', sessionId);
    }

    try {
      const response = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onUploadComplete(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="file-upload">
      <h2 className="section-title">Upload Your Galaxy Data</h2>
      <p className="section-subtitle">
        Upload a CSV file containing your galaxy data. The file should include columns
        that correspond to the available parameters.
      </p>

      <p className="deletion-note">
        🔒 Your data will be deleted after Step 6 (View Results), or automatically
        within 30 minutes — it is never stored permanently.
      </p>

      <div
        className={`upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-input"
          accept=".csv"
          onChange={handleChange}
          style={{ display: 'none' }}
        />
        
        <div className="upload-content">
          {file ? (
            <>
              <div className="file-icon">📄</div>
              <p className="file-name">{file.name}</p>
              <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
              <button 
                className="button button-secondary"
                onClick={() => document.getElementById('file-input').click()}
              >
                Choose Different File
              </button>
            </>
          ) : (
            <>
              <div className="upload-icon">📤</div>
              <p>Drag and drop your CSV file here, or</p>
              <button 
                className="button"
                onClick={() => document.getElementById('file-input').click()}
              >
                Browse Files
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {file && (
        <div className="upload-actions">
          <button
            className="button"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload and Continue'}
          </button>
        </div>
      )}

      {uploading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Processing your file...</p>
        </div>
      )}
    </div>
  );
}

export default FileUpload;
